/**
 * @coeldery/event-reconciliation — 剩貨去向（受控代碼 ＋ 落點規則）
 *
 * 代碼**對應 `destinations` 表** `kind = 'disposition'` 嗰批（種子見 `migrations/0005_seed.sql`）——
 * 代碼係**契約**（規則要靠佢判斷），中文／英文標籤就住喺 DB（由 API 出，前端按語言揀）。
 *
 * ⚠️ 落點字串（`roadshow:<單號>`／`store:<code>`／`warehouse`…）必須通過
 *    `@coeldery/stock-ledger` 嘅 `isLocation()`；本模組**唔 import** 佢
 *    （同 `@coeldery/outbound-order` 一致：模組之間唔互相依賴，由測試做交叉斷言）。
 *
 * 依據：`rules §七` G2（去向逐項必填／轉場要指定目標單）、G6（退回倉只准未開箱整箱）、G7（散件可去邊）
 */

export const DISPOSITION_CODES = [
  'NEXT_ROADSHOW',
  'BACK_TO_WAREHOUSE',
  'TO_STORE',
  'TO_OFFICE',
  'TO_SUPPLIER',
  'WRITTEN_OFF',
] as const;
export type DispositionCode = (typeof DISPOSITION_CODES)[number];

/** 預設去向（產品負責人 2026-09-25 定：剩貨**預設轉下一場**） */
export const DEFAULT_DISPOSITION: DispositionCode = 'NEXT_ROADSHOW';

export const isDispositionCode = (v: unknown): v is DispositionCode =>
  (DISPOSITION_CODES as readonly string[]).includes(String(v));

export interface DispositionRule {
  /** 目標：`order` ＝ 要指定「轉去邊張出庫單」；`store` ＝ 要指定店代碼；`null` ＝ 唔需要 */
  target: 'order' | 'store' | null;
  /** 准唔准散件落呢個去向（⭐ G6：得 `BACK_TO_WAREHOUSE` 唔准 —— 散件冇得入物流倉） */
  allowsUnits: boolean;
  /** 要唔要填原因（⭐ G5：損毀註銷） */
  needsNote: boolean;
  /** 固定落點（`null` ＝ 要靠 `target` 砌） */
  location: string | null;
}

export const DISPOSITION_RULES: Record<DispositionCode, DispositionRule> = {
  NEXT_ROADSHOW: { target: 'order', allowsUnits: true, needsNote: false, location: null },
  BACK_TO_WAREHOUSE: { target: null, allowsUnits: false, needsNote: false, location: 'warehouse' },
  TO_STORE: { target: 'store', allowsUnits: true, needsNote: false, location: null },
  TO_OFFICE: { target: null, allowsUnits: true, needsNote: false, location: 'office' },
  TO_SUPPLIER: { target: null, allowsUnits: true, needsNote: false, location: 'supplier' },
  WRITTEN_OFF: { target: null, allowsUnits: true, needsNote: true, location: 'written_off' },
};

export interface DispositionTarget {
  /** `NEXT_ROADSHOW`：目標出庫單號（例 `SO-2026-0007`） */
  orderNo?: string | null;
  /** `TO_STORE`：店代碼（例 `JHC`） */
  storeCode?: string | null;
}

/**
 * 去向 → 落點（**唯一實作**；route／前端唔准自己駁字串）。
 * 回 `null` ＝ 未夠料（例 `NEXT_ROADSHOW` 未揀目標單）→ 呼叫者當「未填齊」處理。
 */
export function dispositionLocation(code: unknown, target: DispositionTarget = {}): string | null {
  if (!isDispositionCode(code)) return null;
  const rule = DISPOSITION_RULES[code];
  if (rule.target === 'order') {
    const no = String(target.orderNo ?? '').trim();
    return no ? `roadshow:${no}` : null;
  }
  if (rule.target === 'store') {
    const code2 = String(target.storeCode ?? '').trim();
    return code2 ? `store:${code2}` : null;
  }
  return rule.location;
}
